import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import moment from "moment";
import { Dispatch } from "redux";
import Select from "react-select";
import { saveAs } from "file-saver";
import { IStore } from "redux/store";
import { vesselSelectors } from "redux/app/vessel";
import reportActions from "redux/app/report/actions";
import reportSelectors from "redux/app/report/selectors";
import { IRefrigerantCustomSearchRecord } from "redux/app/report/@types";

type VesselOption = {
	value: string;
	label: string;
};

function formatDate(iso: string): string {
	// const d = new Date(iso);
	// const dd = String(d.getDate()).padStart(2, "0");
	// const mm = String(d.getMonth() + 1).padStart(2, "0");
	// const yyyy = d.getFullYear();
	// return `${dd}/${mm}/${yyyy}`;
	return moment(iso).format("DD/MM/YYYY");
}

function exportToCSV(
	records: IRefrigerantCustomSearchRecord[],
	fromDate: string,
	toDate: string,
) {
	const headers = [
		"S.No",
		"Vessel",
		"IMO",
		"Date",
		"Refrigerant Type",
		"Purchased (Kgs)",
		"Consumed (Kgs)",
		"Leaks (Kgs)",
		"Remarks",
		"State",
	];

	const lines = [
		headers.join(","),
		...records.map((r, i) =>
			[
				i + 1,
				`"${r.vessel.vesselName}"`,
				r.vessel.imoNumber,
				formatDate(r.selectedDate),
				r.refrigerantType,
				r.purchased,
				r.consumed,
				r.leaks,
				`"${r.remarks ?? ""}"`,
				r.state,
			].join(","),
		),
	];

	const blob = new Blob([lines.join("\n")], {
		type: "text/csv;charset=utf-8;",
	});
	saveAs(blob, `refrigerant_custom_search_${fromDate}_to_${toDate}.csv`);
}

type OwnProps = {};
type RefrigerantCustomSearchProps = PropsFromRedux & OwnProps;

function RefrigerantCustomSearch(props: RefrigerantCustomSearchProps) {
	const { miniVessels, isLoading, allVesselIds, searchResults } = props;
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [selectedVessels, setSelectedVessels] = useState<VesselOption[]>([]);

	const vesselOptions: VesselOption[] = miniVessels.ids.map((id) => ({
		value: miniVessels.byIds[id].id,
		label: miniVessels.byIds[id].vesselName.toUpperCase(),
	}));

	const isSearchDisabled = !fromDate || !toDate || isLoading;

	useEffect(() => {
		return () => {
			props.resetSearchResults();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleReset() {
		setFromDate("");
		setToDate("");
		setSelectedVessels([]);
		props.resetSearchResults();
	}

	function handleSearch() {
		const vesselIds =
			selectedVessels.length > 0
				? selectedVessels.map((v) => v.value)
				: allVesselIds;
		props.loadCustomSearch(fromDate, toDate, vesselIds);
	}

	return (
		<div className="border-t border-[#D3D3D3] w-full">
			{/* Search bar */}
			<div className="p-6">
				<div className="flex flex-row items-end gap-4">
					<div className="flex flex-col gap-1">
						<label className="text-sm font-medium text-gray-600">From</label>
						<input
							type="date"
							value={fromDate}
							onChange={(e) => setFromDate(e.target.value)}
							max={toDate || undefined}
							className="px-3 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className="text-sm font-medium text-gray-600">To</label>
						<input
							type="date"
							value={toDate}
							onChange={(e) => setToDate(e.target.value)}
							min={fromDate || undefined}
							className="px-3 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
						/>
					</div>
					<div className="flex flex-col gap-1 w-80">
						<label className="text-sm font-medium text-gray-600">Vessels</label>
						<Select
							isMulti
							options={vesselOptions}
							value={selectedVessels}
							onChange={(selected) =>
								setSelectedVessels(selected as VesselOption[])
							}
							placeholder="Select vessels..."
							classNamePrefix="rs"
							styles={{
								control: (base) => ({
									...base,
									minHeight: "38px",
									fontSize: "14px",
									borderColor: "#D1D5DB",
									backgroundColor: "#F9FAFB",
									boxShadow: "none",
									borderRadius: "6px",
									"&:hover": { borderColor: "#9CA3AF" },
								}),
								option: (base, state) => ({
									...base,
									fontSize: "13px",
									backgroundColor: state.isSelected
										? "#BFDBFE"
										: state.isFocused
											? "#F3F4F6"
											: "white",
									color: "#1F2937",
								}),
								multiValue: (base) => ({
									...base,
									backgroundColor: "#DBEAFE",
								}),
								multiValueLabel: (base) => ({
									...base,
									fontSize: "12px",
									color: "#1E40AF",
								}),
								multiValueRemove: (base) => ({
									...base,
									color: "#1E40AF",
									"&:hover": { backgroundColor: "#BFDBFE", color: "#1E3A8A" },
								}),
							}}
						/>
					</div>
					<button
						onClick={handleSearch}
						disabled={isSearchDisabled}
						className="px-5 py-2 text-sm font-medium rounded-md transition-colors duration-200 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
					>
						{isLoading ? "Searching..." : "Search"}
					</button>
					<button
						onClick={handleReset}
						disabled={isLoading}
						className="px-5 py-2 text-sm font-medium rounded-md transition-colors duration-200 border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
					>
						Reset
					</button>
				</div>
			</div>

			{/* Loading */}
			{isLoading && (
				<div className="flex justify-center items-center py-16 text-sm text-gray-500">
					Loading results...
				</div>
			)}

			{/* Empty state */}
			{!isLoading && searchResults.length === 0 && (
				<div className="flex justify-center items-center py-16 text-sm text-gray-400">
					No results. Select a date range and click Search.
				</div>
			)}

			{/* Results table */}
			{!isLoading && searchResults.length > 0 && (
				<div className="px-6 pb-6">
					<div className="flex items-center justify-between mb-3">
						<span className="text-sm text-gray-500">
							{searchResults.length} record
							{searchResults.length !== 1 ? "s" : ""} found
						</span>
						<button
							onClick={() => exportToCSV(searchResults, fromDate, toDate)}
							className="p-3 px-6 py-1 mt-1 mr-1 text-base text-indigo-700 border border-indigo-600 rounded-sm outline-none ws-button-og"
						>
							<span className="mr-3 text-sm">Export Data</span>
							<span className="mb-0.5 text-sm bp3-icon bp3-icon-download"></span>
						</button>
					</div>

					<div className="overflow-x-auto border border-gray-200 rounded-md">
						<table className="w-full text-xs border-collapse">
							<thead>
								<tr className="bg-gray-100 border-b border-gray-300 text-left">
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap">
										S.No
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap">
										Vessel
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap">
										IMO
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap">
										Date
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap">
										Refrigerant Type
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap text-right">
										Purchased (Kgs)
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap text-right">
										Consumed (Kgs)
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200 whitespace-nowrap text-right">
										Leaks (Kgs)
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 border-r border-gray-200">
										Remarks
									</th>
									<th className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">
										State
									</th>
								</tr>
							</thead>
							<tbody>
								{searchResults.map((record, index) => (
									<tr
										key={record._id}
										className="border-b border-gray-200 hover:bg-gray-50"
									>
										<td className="px-3 py-2 text-center text-gray-500 border-r border-gray-200 whitespace-nowrap">
											{index + 1}
										</td>
										<td className="px-3 py-2 font-medium text-gray-800 border-r border-gray-200 whitespace-nowrap">
											{record.vessel.vesselName.toLocaleUpperCase()}
										</td>
										<td className="px-3 py-2 text-gray-600 border-r border-gray-200 whitespace-nowrap">
											{record.vessel.imoNumber}
										</td>
										<td className="px-3 py-2 text-gray-600 border-r border-gray-200 whitespace-nowrap">
											{formatDate(record.selectedDate)}
										</td>
										<td className="px-3 py-2 text-gray-600 border-r border-gray-200 whitespace-nowrap">
											{record.refrigerantType}
										</td>
										<td className="px-3 py-2 text-right text-gray-800 border-r border-gray-200 whitespace-nowrap">
											{record.purchased != null
												? record.purchased.toLocaleString()
												: "-"}
										</td>
										<td className="px-3 py-2 text-right text-gray-800 border-r border-gray-200 whitespace-nowrap">
											{record.consumed != null
												? record.consumed.toLocaleString()
												: "-"}
										</td>
										<td className="px-3 py-2 text-right text-gray-800 border-r border-gray-200 whitespace-nowrap">
											{record.leaks != null
												? record.leaks.toLocaleString()
												: "-"}
										</td>
										<td className="px-3 py-2 text-gray-500 border-r border-gray-200 max-w-xs truncate">
											{record.remarks || "—"}
										</td>
										<td className="px-3 py-2 whitespace-nowrap">
											<span
												className={
													record.state === "APPROVED"
														? "inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
														: "inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"
												}
											>
												{record.state}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
}

function mapStateToProps(store: IStore, _ownProps: OwnProps) {
	const vesselStore = vesselSelectors._getStore(store);
	const reportStore = reportSelectors._getStore(store);
	const vessels = vesselSelectors.getVessels(vesselStore);
	return {
		miniVessels: vesselSelectors.getMiniVessels(vessels),
		allVesselIds: vessels.ids,
		isLoading:
			reportSelectors.jobToLoadRefrigerantCustomSearch(reportStore).isLoading,
		searchResults: reportSelectors.getRefrigerantCustomSearchData(
			reportStore,
		) as IRefrigerantCustomSearchRecord[],
	};
}
function mapDispatchToProps(dispatch: Dispatch, _ownProps: OwnProps) {
	return {
		loadCustomSearch(fromDate: string, toDate: string, vesselIds: string[]) {
			dispatch(
				reportActions.commands.refrigerantCustomSearchLoad(
					fromDate,
					toDate,
					vesselIds,
				),
			);
		},
		resetSearchResults() {
			dispatch(reportActions.document.refrigerantCustomSearchDataSet([]));
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(RefrigerantCustomSearch);
